import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import { SelectField } from "../../components/site/FormBits";
import { FIELD_LABELS, FIELD_OPTIONS } from "../../data/options";
import { SECTORS } from "../../data/sectors";
import { api, apiError } from "../../lib/api";

const FIELDS = Object.entries(FIELD_LABELS).map(([value, label]) => ({ value, label }));
const OPS = [{ value: "in", label: "est l'une de" }, { value: "not_in", label: "n'est aucune de" }];
const SECTOR_OPTS = SECTORS.map((s) => ({ value: s.slug, label: s.name }));
const JOB_OPTS = [...new Map(SECTORS.flatMap((s) => s.jobs.map((j) => [j.slug, { value: j.slug, label: j.name.replace(/ — .*/, "") }]))).values()];
export const optionsFor = (field) => FIELD_OPTIONS[field] || (field === "secteur" ? SECTOR_OPTS : JOB_OPTS);

const ConditionRow = ({ c, onChange, onRemove, i }) => {
  const toggle = (v) => onChange({ ...c, values: c.values.includes(v) ? c.values.filter((x) => x !== v) : [...c.values, v] });
  return (
    <div className="space-y-3 rounded-xl border border-[#EDE3D9] p-4" data-testid={`rule-condition-${i}`}>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <SelectField value={c.field} onChange={(field) => onChange({ field, op: c.op, values: [] })} options={FIELDS} testid={`rule-condition-${i}-field`} />
        <SelectField value={c.op} onChange={(op) => onChange({ ...c, op })} options={OPS} testid={`rule-condition-${i}-op`} />
        <button onClick={onRemove} className="text-[#b42318]" aria-label="Retirer" data-testid={`rule-condition-${i}-remove`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="flex max-h-44 flex-wrap gap-x-5 gap-y-2 overflow-y-auto">
        {optionsFor(c.field).map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <Checkbox checked={c.values.includes(o.value)} onCheckedChange={() => toggle(o.value)} data-testid={`rule-condition-${i}-value-${o.value}`} />{o.label}
          </label>
        ))}
      </div>
    </div>
  );
};

const blank = (forfaits) => ({ name: "", forfait_slug: forfaits[0]?.slug || "", priority: 50, enabled: true, approved: false,
  explanation: "", conditions: [{ field: "statut", op: "in", values: [] }] });

export const RuleEditor = ({ rule, forfaits, onClose, onSaved }) => {
  const [r, setR] = useState(() => (rule ? JSON.parse(JSON.stringify(rule)) : blank(forfaits)));
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setR((p) => ({ ...p, [k]: v }));
  const setCond = (i, c) => set("conditions")(r.conditions.map((x, j) => (j === i ? c : x)));

  const save = async () => {
    if (r.conditions.some((c) => !c.values.length)) return toast.error("Chaque condition doit avoir au moins une valeur.");
    setBusy(true);
    try {
      const body = { name: r.name, forfait_slug: r.forfait_slug, priority: Number(r.priority), enabled: r.enabled, approved: r.approved, explanation: r.explanation, conditions: r.conditions };
      if (rule) await api.put(`/admin/rules/${rule.id}`, body); else await api.post("/admin/rules", body);
      toast.success("Règle enregistrée — nouvelle version créée");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-[#FFFDFB]" data-testid="rule-editor">
        <DialogHeader><DialogTitle className="font-display text-2xl">{rule ? "Modifier la règle" : "Nouvelle règle"}</DialogTitle>
          <DialogDescription>Toutes les conditions doivent être remplies pour que la règle s'applique.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <input className="field-input" placeholder="Nom de la règle" value={r.name} onChange={(e) => set("name")(e.target.value)} data-testid="rule-name-input" />
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <SelectField value={r.forfait_slug} onChange={set("forfait_slug")} options={forfaits.map((f) => ({ value: f.slug, label: f.name }))} testid="rule-forfait-select" />
            <input type="number" min={1} max={1000} className="field-input" value={r.priority} onChange={(e) => set("priority")(e.target.value)} title="Priorité (1 = la plus forte)" data-testid="rule-priority-input" />
          </div>
          <p className="text-xs text-[#242020]/60">Priorité : plus le nombre est petit, plus la règle est forte. Départage : priorité, puis règle la plus spécifique (plus de conditions), puis la plus ancienne.</p>
          {r.conditions.map((c, i) => (
            <ConditionRow key={i} i={i} c={c} onChange={(nc) => setCond(i, nc)} onRemove={() => set("conditions")(r.conditions.filter((_, j) => j !== i))} />
          ))}
          <button onClick={() => set("conditions")([...r.conditions, { field: "objectif", op: "in", values: [] }])} className="btn-outline !py-2 text-sm" data-testid="rule-add-condition"><Plus className="h-4 w-4" />Ajouter une condition</button>
          <textarea rows={3} className="field-input" placeholder="Explication affichée au visiteur (Pourquoi ce forfait pourrait vous convenir?)" value={r.explanation} onChange={(e) => set("explanation")(e.target.value)} data-testid="rule-explanation-input" />
          <div className="flex flex-wrap gap-6 text-sm">
            <label className="flex items-center gap-2"><Switch checked={r.enabled} onCheckedChange={set("enabled")} data-testid="rule-enabled-switch" />Activée</label>
            <label className="flex items-center gap-2"><Switch checked={r.approved} onCheckedChange={set("approved")} data-testid="rule-approved-switch" />Approuvée pour le site public</label>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="btn-outline !py-2.5">Annuler</button>
          <button onClick={save} disabled={busy} className="btn-primary !py-2.5" data-testid="rule-save-button">Enregistrer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
